import { useCallback, useEffect, useState } from 'react'
import {
  Button,
  Group,
  Modal,
  NumberInput,
  Stack,
  Text,
  TextInput,
  ActionIcon,
  Table,
} from '@mantine/core'
import { IconPlus, IconTrash } from '@tabler/icons-react'
import type { AdditionalGuestType, SeatingTestGuestDraft } from '@/lib/types'
import { createTestGuestDraft } from '@/lib/seatingUtils'

interface TestGuestFormEntry {
  id: string
  name: string
  type: AdditionalGuestType
  age?: number | null
}

interface TestGuestModalProps {
  opened: boolean
  onClose: () => void
  initialDraft?: SeatingTestGuestDraft | null
  onSubmit: (draft: SeatingTestGuestDraft) => void
}

function draftToFormEntries(draft: SeatingTestGuestDraft | null | undefined): TestGuestFormEntry[] {
  return (draft?.additional_guests ?? []).map((ag) => ({
    id: ag.id,
    name: ag.name,
    type: ag.type,
    age: ag.age,
  }))
}

function getInputValue(value: string | React.ChangeEvent<HTMLInputElement>): string {
  return typeof value === 'string' ? value : (value.target?.value ?? '')
}

export function TestGuestModal({ opened, onClose, initialDraft, onSubmit }: TestGuestModalProps) {
  const isEditing = Boolean(initialDraft)
  const [primaryName, setPrimaryName] = useState('')
  const [additionalGuests, setAdditionalGuests] = useState<TestGuestFormEntry[]>([])
  const [plusOneModalOpen, setPlusOneModalOpen] = useState(false)
  const [childModalOpen, setChildModalOpen] = useState(false)
  const [plusOneName, setPlusOneName] = useState('')
  const [childName, setChildName] = useState('')
  const [childAge, setChildAge] = useState<number | ''>('')

  useEffect(() => {
    if (!opened) return
    setPrimaryName(initialDraft?.primary_name ?? '')
    setAdditionalGuests(draftToFormEntries(initialDraft))
    setPlusOneModalOpen(false)
    setChildModalOpen(false)
    setPlusOneName('')
    setChildName('')
    setChildAge('')
  }, [opened, initialDraft])

  const handleRemoveAdditional = useCallback((id: string) => {
    setAdditionalGuests((prev) => prev.filter((entry) => entry.id !== id))
  }, [])

  const handleAddPlusOne = useCallback(() => {
    const trimmedName = plusOneName.trim()
    if (!trimmedName) return

    setAdditionalGuests((prev) => [
      ...prev,
      { id: crypto.randomUUID(), name: trimmedName, type: 'plus_one' },
    ])
    setPlusOneName('')
    setPlusOneModalOpen(false)
  }, [plusOneName])

  const handleAddChild = useCallback(() => {
    const trimmedName = childName.trim()
    if (!trimmedName || childAge === '') return

    setAdditionalGuests((prev) => [
      ...prev,
      {
        id: crypto.randomUUID(),
        name: trimmedName,
        type: 'child',
        age: Number(childAge),
      },
    ])
    setChildName('')
    setChildAge('')
    setChildModalOpen(false)
  }, [childAge, childName])

  const handleSubmit = useCallback(() => {
    const trimmedName = primaryName.trim()
    if (!trimmedName) return

    const draft = createTestGuestDraft(trimmedName, additionalGuests)
    if (initialDraft) {
      draft.id = initialDraft.id
    }

    onSubmit(draft)
    onClose()
  }, [additionalGuests, initialDraft, onClose, onSubmit, primaryName])

  return (
    <>
      <Modal
        opened={opened}
        onClose={onClose}
        title={isEditing ? 'Testnutzer bearbeiten' : 'Testnutzer hinzufügen'}
        size="md"
      >
        <form
          onSubmit={(event) => {
            event.preventDefault()
            handleSubmit()
          }}
        >
          <Stack gap="sm">
            <TextInput
              label="Name"
              placeholder="Name des Testnutzers"
              value={primaryName}
              onChange={(event) => setPrimaryName(getInputValue(event))}
              required
              autoFocus
            />

            <Stack gap="xs">
              <Group justify="space-between" align="center">
                <Text size="sm" fw={600}>
                  Begleitpersonen
                </Text>
                <Group gap="xs">
                  <Button
                    size="compact-xs"
                    variant="light"
                    leftSection={<IconPlus size={14} />}
                    onClick={() => setPlusOneModalOpen(true)}
                    type="button"
                  >
                    +1
                  </Button>
                  <Button
                    size="compact-xs"
                    variant="light"
                    leftSection={<IconPlus size={14} />}
                    onClick={() => setChildModalOpen(true)}
                    type="button"
                  >
                    Kind
                  </Button>
                </Group>
              </Group>

              {additionalGuests.length === 0 ? (
                <Text size="xs" c="dimmed">
                  Keine +1 oder Kinder
                </Text>
              ) : (
                <Table withTableBorder withColumnBorders>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Typ</Table.Th>
                      <Table.Th>Name</Table.Th>
                      <Table.Th w={60} />
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {additionalGuests.map((entry) => (
                      <Table.Tr key={entry.id}>
                        <Table.Td>{entry.type === 'plus_one' ? '+1' : 'Kind'}</Table.Td>
                        <Table.Td>
                          {entry.name}
                          {entry.type === 'child' && entry.age != null ? ` (${entry.age})` : ''}
                        </Table.Td>
                        <Table.Td>
                          <ActionIcon
                            color="red"
                            variant="subtle"
                            onClick={() => handleRemoveAdditional(entry.id)}
                            aria-label="Entfernen"
                          >
                            <IconTrash size={16} />
                          </ActionIcon>
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              )}
            </Stack>

            <Group justify="flex-end" mt="sm">
              <Button variant="default" onClick={onClose} type="button">
                Abbrechen
              </Button>
              <Button type="submit" disabled={!primaryName.trim()}>
                {isEditing ? 'Speichern' : 'Hinzufügen'}
              </Button>
            </Group>
          </Stack>
        </form>
      </Modal>

      <Modal
        opened={plusOneModalOpen}
        onClose={() => {
          setPlusOneModalOpen(false)
          setPlusOneName('')
        }}
        title="+1 hinzufügen"
        size="sm"
        centered
      >
        <form
          onSubmit={(event) => {
            event.preventDefault()
            handleAddPlusOne()
          }}
        >
          <Stack gap="sm">
            <TextInput
              label="Name der Begleitung"
              placeholder="Name eingeben"
              value={plusOneName}
              onChange={(event) => setPlusOneName(getInputValue(event))}
              required
              autoFocus
            />
            <Group justify="flex-end">
              <Button
                variant="default"
                type="button"
                onClick={() => {
                  setPlusOneModalOpen(false)
                  setPlusOneName('')
                }}
              >
                Abbrechen
              </Button>
              <Button type="submit" disabled={!plusOneName.trim()}>
                Hinzufügen
              </Button>
            </Group>
          </Stack>
        </form>
      </Modal>

      <Modal
        opened={childModalOpen}
        onClose={() => {
          setChildModalOpen(false)
          setChildName('')
          setChildAge('')
        }}
        title="Kind hinzufügen"
        size="sm"
        centered
      >
        <form
          onSubmit={(event) => {
            event.preventDefault()
            handleAddChild()
          }}
        >
          <Stack gap="sm">
            <TextInput
              label="Name des Kindes"
              placeholder="Name eingeben"
              value={childName}
              onChange={(event) => setChildName(getInputValue(event))}
              required
              autoFocus
            />
            <NumberInput
              label="Alter"
              placeholder="Alter eingeben"
              value={childAge}
              onChange={(value) => setChildAge(typeof value === 'number' ? value : '')}
              min={0}
              max={17}
              required
            />
            <Group justify="flex-end">
              <Button
                variant="default"
                type="button"
                onClick={() => {
                  setChildModalOpen(false)
                  setChildName('')
                  setChildAge('')
                }}
              >
                Abbrechen
              </Button>
              <Button type="submit" disabled={!childName.trim() || childAge === ''}>
                Hinzufügen
              </Button>
            </Group>
          </Stack>
        </form>
      </Modal>
    </>
  )
}
